import { FC, FormEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { History, Restore, SaveAlt, Visibility, VisibilityOff } from '@mui/icons-material'
import { Alert, Box, Button, IconButton, Tooltip, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { usePrompt } from '@hooks'

interface DraftProps {
  hasDraft: boolean
  draftSavedAt?: number
  onRestoreDraft: () => void
  onDiscardDraft: () => void
  onSaveDraft: () => void
}

interface MobileConfiguratorLayoutProps {
  title: string
  isEdit: boolean
  onSubmit: (e: FormEvent) => void | Promise<void>
  onCancel: () => void
  onReset?: () => void
  isDirty?: boolean
  children: ReactNode
  previewComponent?: ReactNode
  errors?: string[]
  draftProps?: DraftProps
}

const CONFIGURATOR_FORM_ID = 'mobile-configurator-form'

const formatDraftTime = (savedAt: number): string => {
  const date = new Date(savedAt)

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const MobileConfiguratorLayout: FC<MobileConfiguratorLayoutProps> = ({
  title,
  isEdit,
  onSubmit,
  onCancel,
  onReset,
  isDirty = false,
  children,
  previewComponent,
  errors = [],
  draftProps,
}) => {
  const [previewVisible, setPreviewVisible] = useState(true)
  const errorsRef = useRef<HTMLDivElement>(null)
  const skipLeavePromptRef = useRef(false)

  usePrompt({ when: isDirty, skipRef: skipLeavePromptRef })

  useEffect(() => {
    if (!isDirty) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const errorSignature = errors.join('\n')

  useEffect(() => {
    if (!errorSignature) return

    errorsRef.current?.scrollIntoView({ block: 'nearest' })
  }, [errorSignature])

  const handleFormSubmit = (event: FormEvent) => {
    if (!isDirty) {
      event.preventDefault()

      return
    }

    // Create после сохранения сам делает navigate. Пока идёт submit, подтверждение ухода не нужно.
    skipLeavePromptRef.current = true
    try {
      Promise.resolve(onSubmit(event)).finally(() => {
        skipLeavePromptRef.current = false
      })
    } catch (error) {
      skipLeavePromptRef.current = false
      throw error
    }
  }

  return (
    <PageContentLayout
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        // 100vh вылезает из-под AppBar и футера, и панель действий уезжает за экран.
        height: '100%',
        minHeight: 0,
        p: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                {title}
              </Typography>
            }
          />

          {draftProps?.hasDraft && (
            <Alert
              severity='info'
              icon={<History />}
              sx={{ mb: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size='small'
                    color='inherit'
                    startIcon={<Restore />}
                    onClick={draftProps.onRestoreDraft}
                  >
                    Restore
                  </Button>
                  <Button size='small' color='inherit' onClick={draftProps.onDiscardDraft}>
                    Discard
                  </Button>
                </Box>
              }
            >
              Local draft
              {draftProps.draftSavedAt ? ` from ${formatDraftTime(draftProps.draftSavedAt)}` : ''}
            </Alert>
          )}

          {errors.length > 0 && (
            <Alert ref={errorsRef} severity='error' sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <Typography key={`${error}-${index}`} variant='body2'>
                  {error}
                </Typography>
              ))}
            </Alert>
          )}

          <form id={CONFIGURATOR_FORM_ID} onSubmit={handleFormSubmit}>
            {children}
          </form>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 2,
            px: 3,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            alignItems: 'center',
          }}
        >
          <Button
            type='submit'
            form={CONFIGURATOR_FORM_ID}
            variant='contained'
            color='primary'
            disabled={!isDirty}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
          {onReset && (
            <Button
              type='button'
              variant='outlined'
              color='warning'
              onClick={onReset}
              disabled={!isDirty}
            >
              Reset
            </Button>
          )}
          <Button type='button' variant='outlined' onClick={onCancel}>
            Cancel
          </Button>
          {draftProps && (
            <Tooltip title='Save current changes as a local draft'>
              <span>
                <Button
                  type='button'
                  variant='outlined'
                  color='secondary'
                  startIcon={<SaveAlt />}
                  onClick={draftProps.onSaveDraft}
                  disabled={!isDirty}
                >
                  Save as Draft
                </Button>
              </span>
            </Tooltip>
          )}
          {previewComponent && (
            <Tooltip title={previewVisible ? 'Hide Preview' : 'Show Preview'}>
              <IconButton onClick={() => setPreviewVisible(!previewVisible)} color='primary'>
                {previewVisible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {previewVisible && previewComponent && (
        <Box
          sx={{
            width: 'min(400px, 42%)',
            flexShrink: 0,
            overflow: 'auto',
            p: 2,
            borderLeft: '1px solid',
            borderColor: 'divider',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%',
          }}
        >
          {previewComponent}
        </Box>
      )}
    </PageContentLayout>
  )
}
